import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { when } from "lit/directives/when.js";

import { Watch } from "@/decorators/watch";
import { filterElementsByTagNames } from "@/internals/elements";

import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";
import type { ListType } from "./types";
import styles from "./atlas-list.scss";

export type ListProps = AtlasElementProps & {
    "gap": number;
    "type": ListType;
    "marker-alignment": "left" | "right";
};

/**
 * @slot - Usado para colocar os itens da lista (atlas-list-item)
 *
 * @tag atlas-list
 */
@customElement("atlas-list")
export default class AtlasList extends AtlasElement {
    static styles = styles;

    /** Define o espaçamento entre os itens da lista (Deve ser um número entre 0 e 3) */
    @property({ type: Number }) gap: number = 0;

    /** Define o tipo de lista */
    @property({ type: String }) type: ListType = "default";

    /** Define o alinhamento do marcador dos itens da lista */
    @property({ type: String, attribute: "marker-alignment" }) markerAlignment: "left" | "right" = "left";

    /**
     * @internal
     * Define se a lista é aninhada
     */
    @property({ type: Boolean, reflect: true }) nested: boolean = false;

    /**
     * @internal
     * Define o nível de aninhamento da lista
     */
    @property({ type: Number, attribute: null }) nestedLevel: number = 0;

    /**
     * @internal
     */
    @Watch("type", true)
    public async onTypeChange() {
        await this.updateComplete;

        this.setPropertiesInNestedLists();
        this.setPropertiesInListItems();
    }

    private getNestedLists() {
        const contentSlot = this.shadowRoot.querySelector("slot");
        const slottedElements = contentSlot.assignedElements({ flatten: true });

        return filterElementsByTagNames(slottedElements, ["ATLAS-LIST"]);
    }

    private getNestedListItems() {
        const contentSlot = this.shadowRoot.querySelector("slot");
        const slottedElements = contentSlot.assignedElements({ flatten: true });

        return filterElementsByTagNames(slottedElements, ["ATLAS-LIST-ITEM"]);
    }

    private async setPropertiesInNestedLists() {
        await this.updateComplete;

        this.getNestedLists().forEach((element) => {
            element.nested = true;
            element.gap = this.gap;
            element.nestedLevel = this.nestedLevel + 1;
            element.type = this.type;
            element.markerAlignment = this.markerAlignment;
        });
    }

    private setPropertiesInListItems() {
        this.getNestedListItems().forEach((element) => {
            element.listType = this.type;
            element.listNestedLevel = this.nestedLevel;
            element.markerAlignment = this.markerAlignment;
        });
    }

    private async onSlotChange() {
        await this.updateComplete;

        this.setPropertiesInNestedLists();
        this.setPropertiesInListItems();
    }

    /** @internal */
    public render() {
        const listClass = {
            "atlas-list": true,
            "nested": this.nested,
            [`row-gap-${this.gap}`]: this.gap
        };

        return when(
            this.type === "ordered",
            () => {
                return html`
                    <ol class="${classMap(listClass)}" role="list">
                        <slot @slotchange=${this.onSlotChange}></slot>
                    </ol>
                `;
            },
            () => {
                return html`
                    <ul class="${classMap(listClass)}" role="list">
                        <slot @slotchange=${this.onSlotChange}></slot>
                    </ul>
                `;
            }
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-list": AtlasList;
    }
}
